/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  CalendarListItemDto,
  ScheduleTaskType,
  TaskRelationDto,
  TaskRelationType,
} from '@gh/builder-shared-data';
import {
  composeSetters,
  createArrayHelper,
  DateTimeTypes,
  EntityConfig,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  fieldValidationSetter,
  fieldValidatorByEntityConfig,
  HOURS_IN_DAY,
  NumberTypes,
  setEntityField,
  setEntityFields,
  setUpdatedDirtyType,
} from '@gh/common-shared';
import { AutocompleteItem, ID, WithDirtyType, WithEntityState } from '@gh/common-shared-data';
import {
  addDays,
  addHours,
  cast,
  currentDate,
  currentNormalizedDate,
  diffDays,
  diffTimeHours,
  isDateValid,
  isNotNil,
  isSameDayOfYear,
  newTime,
  normalizeDate,
  setHours,
  shiftDays,
  toString,
} from '@gh/core-util';
import { clamp, isNil, keyBy, negate, omit, overEvery } from 'lodash';
import {
  ScheduleTemplateGanttTask,
} from '../../builder-setup/scheduling/resources/schedule-template-gantt-details.model';
import { isResourceEditable, isScheduleTaskStart } from './schedule.model';

export type GanttTaskRelation = TaskRelationDto & { taskId: ID; id: ID };

export type GanttTask = WithEntityState<WithDirtyType<{
  physicalTaskId: ID;
  scheduleId: ID;
  taskId: ID;
  taskNumber: number;
  name: string;
  todo?: any[];
  type: ScheduleTaskType;
  hasChildrenExcludedFromGanttChart: boolean;
  notes?: any[];
  endDate: Date;
  duration: number;
  parentTaskId?: ID;
  resource?: {
    name?: string;
    id?: ID;
    isCcSupplier?: boolean;
    isEmployee?: boolean;
  };
  recentNote?: {
    memo: string;
    scheduleTaskNoteId: ID;
  };
  noteMemo?: string;
  startDate: Date;
  description?: string;
  predecessors?: GanttTaskRelation[];
  completionPct?: number;
  completionDate?: Date;
  isExcludeFromGanttChart: boolean;
  isFromCriticalPath: boolean;
  isResourceInUse: boolean;
  hasChildren: boolean;
  canBeDeleted: boolean;
  opened: boolean;
  supplierContactId?: ID;
  selected?: boolean;
  mayHaveResource: boolean;
  canBeMarkedAsDeleted: boolean;
  markedAsDeleted: boolean;
  status?: number;
}>>;

export type CalendarListItem = CalendarListItemDto;

export type GanttTimelineTask = {
  id: ID;
  name: string;
  text: string;
  start_date: Date;
  end_date?: Date;
  duration: number;
  progress: number;
  parent?: ID;
  type: string;
  description?: string;
};

export type GanttTimelineLink = {
  id: ID;
  source: ID;
  target: ID;
  type: string;
  lag: number;
};

export type GanttPredecessorListItem = {
  id: number;
  task: AutocompleteItem;
  relation: TaskRelationType;
  lag: number;
  lagType: '+' | '-';
};
export type GanttPredecessorKitDetails = {
  predecessors: GanttTaskRelation[];
};

export type GanttChartBounds = {
  startDate: Date;
  endDate: Date;
  duration: number;
};

export const GANTT_TASK_ROOT_ID = '';
export const HOURS_START_OF_DAY = 8;
export const HOURS_END_OF_DAY = 16;
export const GANTT_WORKING_HOURS = 8;
export const GANTT_SCHEDULE_UNIQUE_ID = 'unique';

export const getGanttTaskParentId = ({ parentTaskId }: GanttTask) => ganttTaskIdToParentId(parentTaskId);
export const ganttTaskIdToParentId = (taskId?: ID) => isNotNil(taskId) ? taskId : GANTT_TASK_ROOT_ID;
export const isGanttTaskRootId = (taskId: any) => taskId === GANTT_TASK_ROOT_ID;
export const getCalendarListItemKey = (item: CalendarListItem) => toString(item.date.getTime());

const TASK_REGULAR_TYPE = 'task';
const TASK_PROJECT_TYPE = 'project';

export const getGanttTaskType = (task: GanttTask) => task.hasChildren ? TASK_PROJECT_TYPE : TASK_REGULAR_TYPE;

export type GanttTaskHierarchyRelation = {
  canBeDeleted: boolean;
  hasChildren: boolean;
  hasChildrenExcludedFromGanttChart: boolean;
};

export const isGanttTaskCanBeDeleted = (task: GanttTask) => {
  if (isGanttStartTask(task)) {
    return false;
  }
  switch (task.type) {
    case ScheduleTaskType.Summary:
      return true;
    case ScheduleTaskType.Empty:
      return true;
    case ScheduleTaskType.CostCenter:
      return !task.isFromCriticalPath;
    default:
      return false;
  }
};

export const isGanttTaskCanBeOpened = (task: GanttTask) => !isGanttStartTask(task) && !task.markedAsDeleted;
export const isGanttTaskCanHaveSubtasks = (task: GanttTask) =>
  task.type !== ScheduleTaskType.CostCenter && !isGanttStartTask(task) && !task.markedAsDeleted;

export const getGanttTaskId = (task: GanttTask) => task.taskId;
export const genGanttTaskId = (scheduleId: ID, taskNo?: number) => `${scheduleId}-${taskNo ? taskNo : 'top'}`;
export const genGanttTaskUniqueId = (taskNo?: number) => genGanttTaskId(GANTT_SCHEDULE_UNIQUE_ID, taskNo);
export const getGanttScheduleIdFromId = (id: ID) => id.split('-')[0];
export const getGanttTaskNumberFromId = (id: ID, tasks: ScheduleTemplateGanttTask[]) =>
  tasks.find((task) => task.taskId === id)?.taskNumber || 1;
export const isGanttTaskIdForSchedule = (taskId: ID, scheduleId: ID) => taskId.startsWith(scheduleId);
export const isGanttTaskTopId = (taskId: ID) => taskId.endsWith('top');

export const ganttTaskArr = createArrayHelper(getGanttTaskId);
export const ganttTaskIdArr = createArrayHelper<ID>();

export const isGanttTaskCompleted = (task: GanttTask) => task.completionPct === 100;
export const isGanttTopTask = (task: GanttTask) => isGanttTaskTopId(getGanttTaskId(task));
export const isGanttTaskNameEditable = (task: GanttTask) => {
  if (isGanttStartTask(task)) {
    return false;
  }
  switch (task.type) {
    case ScheduleTaskType.Summary:
      return true;
    case ScheduleTaskType.Empty:
      return true;
    case ScheduleTaskType.CostCenter:
      return !task.isFromCriticalPath;
    default:
      return false;
  }
};
export const isGanttTaskPredecessorsEditable = (task: GanttTask) => !isGanttStartTask(task) && !task.isExcludeFromGanttChart;
export const isGanttTaskEditable = (task: GanttTask) => !isGanttTopTask(task) && !task.markedAsDeleted;

export const isGanttTaskDurationEditable = (task: GanttTask) => !task.hasChildren;

export const isGanttTaskCompletionPctEditable = (task: GanttTask) => !task.hasChildren;

export const setGanttTaskHasChildren = (task: GanttTask, hasChildren: boolean) => {
  if (hasChildren) {
    return setEntityFields(task, {
      hasChildren,
      type: task.type === ScheduleTaskType.Empty ? ScheduleTaskType.Summary : task.type,
    });
  } else {
    return setEntityFields(task, {
      hasChildren,
      type: task.type === ScheduleTaskType.Summary ? ScheduleTaskType.Empty : task.type,
    });
  }
};

export const isGanttStartTask = (task: GanttTask) => task.taskNumber === 1;

const isGanttTaskEndDateValid = (task: GanttTask, field: string, date: Date) =>
  date <= task.startDate ? { endDate: { messageId: 'msg_incorrect_end_date' } } : void 0;

export const GANTT_TASK_ENTITY_CONFIG: EntityConfig<GanttTask> = {
  name: {
    required: true,
    editable: <any>overEvery(isGanttTaskEditable, isGanttTaskNameEditable),
  },
  predecessors: {
    editable: <any>overEvery(isGanttTaskEditable, isGanttTaskPredecessorsEditable),
  },
  duration: {
    required: true,
    type: NumberTypes.HoursNotZero,
    editable: <any>overEvery(isGanttTaskEditable, isGanttTaskDurationEditable),
  },
  startDate: {
    required: true,
    type: DateTimeTypes.DateTime,
    editable: <any>overEvery(isGanttTaskEditable, isGanttTaskDurationEditable),
  },
  endDate: {
    required: true,
    type: DateTimeTypes.DateTime,
    editable: <any>overEvery(isGanttTaskEditable, isGanttTaskDurationEditable),
    validator: isGanttTaskEndDateValid,
  },
  completionPct: {
    required: true,
    type: NumberTypes.Percents,
    editable: <any>overEvery(isGanttTaskEditable, isGanttTaskCompletionPctEditable),
  },
  confirmedWith: {
    editable: <any>overEvery(isGanttTaskEditable),
  },
  confirmedForDate: {
    required: false,
    type: DateTimeTypes.DateTime,
    editable: <any>overEvery(isGanttTaskEditable),
  },
  confirmedOnDate: {
    required: false,
    type: DateTimeTypes.DateTime,
    editable: <any>overEvery(isGanttTaskEditable),
  },
// @ts-expect-error Legacy typing suppression
  isExcludeFromGanttChart: { editable: <any>overEvery(isGanttTaskEditable, negate(isScheduleTaskStart)) },
// @ts-expect-error Legacy typing suppression
  resource: { editable: <any>overEvery(isGanttTaskEditable, isResourceEditable)},
// @ts-expect-error Legacy typing suppression
  description: { editable: <any>overEvery(isGanttTaskEditable, negate(isScheduleTaskStart))},
};

export const WORK_TIME_HOURS_START = 8;
export const WORK_TIME_HOURS_END = 16;
export const WORK_TIME_START = newTime(WORK_TIME_HOURS_START, 0);
export const WORK_TIME_END = newTime(WORK_TIME_HOURS_END, 0);

export const isGanttWorkTime = (date: Date) => date >= WORK_TIME_START && date <= WORK_TIME_END;

export const ganttTaskFieldState = fieldStateByEntityConfig(GANTT_TASK_ENTITY_CONFIG);
export const validateGanttTaskField = fieldValidatorByEntityConfig(GANTT_TASK_ENTITY_CONFIG);
export const validateGanttTask = entityValidatorByEntityConfig(GANTT_TASK_ENTITY_CONFIG);
export const selectGanttTask = composeSetters(
  setEntityField,
);
export const setGanttTaskField = composeSetters(
  setEntityField,
  setUpdatedDirtyType,
  fieldValidationSetter(validateGanttTaskField));
export const syncGanttCompletionDate = (task: GanttTask) => {
  if (task.completionPct === 100) {
    return isNil(task.completionDate) ? setEntityField(task, 'completionDate', currentDate()) : task;
  } else {
    return setEntityField(task, 'completionDate', void 0);
  }
};
export const setGanttTaskFieldsWithoutDirty = setEntityFields;
export const setGanttTaskFields = (task: GanttTask, fields: Hash<any>) => {
  const updatedTask = setEntityFields(task, fields);
  return task !== updatedTask ? setUpdatedDirtyType(updatedTask) : task;
};

/**
 * Calculates and merges hierarchical properties into each task.
 */
export function calculateHierarchicalRelations<T extends GanttTask>(tasks: T[]): T[] {
  // temporary structure to store task-related data
  const tasksInfo: Hash<GanttTaskHierarchyRelation> = {};

  return <T[]>ganttTaskArr.updateAllRight(
    tasks,
    (task) => {
      const { taskId, parentTaskId, isExcludeFromGanttChart } = task;
      const taskInfo = ensureTaskInfo(taskId);
      // update flag only if it was not set to false
      const canTaskBeDeleted = taskInfo.canBeDeleted === false ? false : isGanttTaskCanBeDeleted(task);
      taskInfo.canBeDeleted = canTaskBeDeleted;
      if (parentTaskId) {
        const parentTaskInfo = ensureTaskInfo(parentTaskId);
        // update flag only if it was not set to false
        parentTaskInfo.canBeDeleted = parentTaskInfo.canBeDeleted === false ? false : canTaskBeDeleted;
        parentTaskInfo.hasChildren = true;
        parentTaskInfo.hasChildrenExcludedFromGanttChart = parentTaskInfo.hasChildrenExcludedFromGanttChart
          || isExcludeFromGanttChart;
      }

      return setEntityFields(task, {
        canBeDeleted: taskInfo.canBeDeleted,
        hasChildren: taskInfo.hasChildren,
        mayHaveResource: mayHaveResource(task, tasks),
        // type of task becomes Empty if it does not have children anymore
        type: task.type === ScheduleTaskType.Summary && !taskInfo.hasChildren ? ScheduleTaskType.Empty : task.type,
        hasChildrenExcludedFromGanttChart: taskInfo.hasChildrenExcludedFromGanttChart,
      });
    });

  function ensureTaskInfo(id: ID): GanttTaskHierarchyRelation {
    const task = tasksInfo[id];
    const taskInfo = task || { canBeDeleted: true, hasChildren: false, hasChildrenExcludedFromGanttChart: false };
    tasksInfo[id] = taskInfo;
    return taskInfo;
  }
}

export const addDuration = (baseDate: Date, duration: number): Date => {
  const normalizedBaseDate = setHours(baseDate, HOURS_START_OF_DAY);
  const normalizedDuration = clamp(baseDate.getHours(), HOURS_START_OF_DAY, HOURS_END_OF_DAY) - HOURS_START_OF_DAY
    + duration;
  const days = Math.floor(normalizedDuration / HOURS_IN_DAY);
  const theRestHours = normalizedDuration % HOURS_IN_DAY;
  return correctEndWorkTime(addHours(
    addDays(normalizeDate(normalizedBaseDate), days),
    HOURS_START_OF_DAY + theRestHours));
};

export const hoursTillEndOfBusinessDay = (date: Date) => HOURS_END_OF_DAY - date.getHours();

/**
 * Corrects date which represent end of work day: if date has hours = 8 =>
 * date should be converted to the previous day having hours = 16
 *
 * @param date
 */
export const correctEndWorkTime = (date: Date): Date =>
  date.getHours() === HOURS_START_OF_DAY ? setHours(shiftDays(date, -1), HOURS_END_OF_DAY) : date;

export const durationDaysToHours = (days: number) => days * HOURS_IN_DAY;

export const getGanttTaskText = (task: GanttTask): string => {
  if (!task) {
    return `<span class="name"></span>`;
  }
  const resource = task.resource?.name ? `<span class="resource ${task.isResourceInUse ? 'resource-conflict' : '' }">${task.resource.name}</span>` : '';
  return `<span class="name">${task.name}</span>${resource}`;
};
/**
 * Copies a Date so the timeline (DHTMLX, which changes task dates in place while scheduling), the state and the
 * undo/redo history never share one Date object: a later timeline change would otherwise rewrite a history snapshot.
 */
export function copyGanttDate<T>(date: T): T {
  return date instanceof Date ? <T><unknown>new Date(date.getTime()) : date;
}

export const ganttTaskToTimelineTask = (task: GanttTask): GanttTimelineTask => ({
  id: task.taskId,
  text: getGanttTaskText(task) ,
  name: task.name,
  description: task.description,
  start_date: isNotNil(task.startDate) && isDateValid(task.startDate) ? copyGanttDate(task.startDate) : currentNormalizedDate(),
  duration: Number.isFinite(task.duration) && task.duration >= 0 ? task.duration : 1,
  progress: (task.completionPct || 0) / 100,
  parent: task.parentTaskId,
  type: getGanttTaskType(task),
});

export const ganttPredecessorListItemFromRelation = (relation: GanttTaskRelation): GanttPredecessorListItem =>
  cast<GanttPredecessorListItem>({
    ...omit(relation, 'taskNo'),
    task: isNotNil(relation.taskNo) ? { id: String(relation.taskId), name: String(relation.taskNo) } : void 0,
    lagType: relation.lag < 0 ? '-' : '+',
    lag: Math.abs(relation.lag),
  });

export const ganttPredecessorListItemToRelation = (relation: GanttPredecessorListItem): GanttTaskRelation => ({
  id: nextLinkId(),
  taskId: relation.task && relation.task.id,
  taskNo: relation.task && Number(relation.task.name),
  relation: relation.relation,
  lag: (relation.lagType === '-' ? -1 : 1) * (relation.lag || 0),
});

export const createEmptyGanttPredecessorListItem = (): GanttPredecessorListItem => <any>({
  id: nextLinkId(),
  task: void 0,
  relation: TaskRelationType.FinishToStart,
  lagType: '+',
});

export const relationTypeToTimelineLinkType = (relationType: TaskRelationType) => {
  switch (relationType) {
    case TaskRelationType.FinishToStart:
      return '0';
    case TaskRelationType.StartToStart:
      return '1';
    case TaskRelationType.FinishToFinish:
      return '2';
    case TaskRelationType.StartToFinish:
      return '3';
    default:
      throw new Error(`Unrecognized type of Gantt task relation: ${relationType}`);
  }
};
export const relationTypeFromTimelineLinkType = (linkType: string): TaskRelationType => {
  switch (linkType) {
    case '0':
      return TaskRelationType.FinishToStart;
    case '1':
      return TaskRelationType.StartToStart;
    case '2':
      return TaskRelationType.FinishToFinish;
    case '3':
      return TaskRelationType.StartToFinish;
    default:
      throw new Error(`Unrecognized type of Gantt timeline link: ${linkType}`);
  }
};

export const ganttTaskRelationToTimelineLink = (taskId: ID, pred: GanttTaskRelation): GanttTimelineLink => ({
  id: pred.id,
  source: pred.taskId,
  target: taskId,
  lag: pred.lag,
  type: relationTypeToTimelineLinkType(pred.relation),
});

export const ganttTaskRelationFromTimelineLink = (link: GanttTimelineLink, tasks: any[]): GanttTaskRelation => ({
  id: link.id,
  taskId: link.source,
  taskNo: getGanttTaskNumberFromId(link.source, tasks),
  lag: link.lag || 0,
  relation: relationTypeFromTimelineLinkType(link.type),
});

let lastLinkId = 0;
export const nextLinkId = () => String(++lastLinkId);

export const ganttTaskRelationFromTask = (tasks: any[],
                                         relation: TaskRelationDto,
                                         dayStep: number = GANTT_WORKING_HOURS,
                                          ): GanttTaskRelation => {
  const target = tasks.find((task) => task.taskNumber === relation.taskNo);
  return {
    id: nextLinkId(),
    taskId: target.taskId || genGanttTaskId(target.scheduleId, relation.taskNo),
    ...relation,
    lag: relation.lag / dayStep,
  };
};
export const ganttTaskRelationFromDto = (scheduleId: ID,
                                         relation: TaskRelationDto,
                                         dayStep: number = GANTT_WORKING_HOURS): GanttTaskRelation => ({
      id: nextLinkId(),
      taskId: genGanttTaskId(scheduleId, relation.taskNo),
  ...relation,
      lag: relation.lag / dayStep,
  });
export const ganttTaskRelationFromTaskDto = (task: any,
                                         relation: TaskRelationDto,
                                         dayStep: number = GANTT_WORKING_HOURS): GanttTaskRelation =>
  ({
    id: nextLinkId(),
    taskId: task.id,
    ...relation,
    lag: relation.lag / dayStep,
  });

export const ganttTaskRelationToDto = (relation: GanttTaskRelation): TaskRelationDto => ({
  ...<TaskRelationDto>omit(relation, 'id', 'taskId', 'lag'),
  lag: relation.lag * GANTT_WORKING_HOURS,
});

export const normalizeTaskRelations = (newRelations: GanttTaskRelation[],
                                       oldRelations: GanttTaskRelation[]): GanttTaskRelation[] => {
  const oldRelationIndex = keyBy(oldRelations, 'taskNo');

  return newRelations.map((newRelation) => {
    const oldRelation = oldRelationIndex[newRelation.taskNo];
    return oldRelation ? { ...newRelation, id: oldRelation.id } : newRelation;
  });
};

export const completionPctToTimelineTaskProgress = (completionPct: number) => completionPct / 100;

export const completionPctFromTimelineTaskProgress = (completionPct: number) => completionPct * 100;

export const isGanttTaskIncluded = (task: GanttTask) => !task?.isExcludeFromGanttChart && !task?.markedAsDeleted;
export const mayHaveResource = (task: GanttTask, tasks: any[]): boolean => {
  if (task.type === ScheduleTaskType.CostCenter || task.type === ScheduleTaskType.Empty) {
    return true;
  }
  const isChild = (item: any): boolean => {
    if (!item || !item.parentTaskId) {
      return false;
    }
    if (item.parentTaskId === task.taskId) {
      if (item.type === ScheduleTaskType.CostCenter || item.type === ScheduleTaskType.Empty) {
        return true;
      }
      return false;
    }
    const target = tasks.find((a) => a.taskId === item.parentTaskId);
    return isChild(target);
  };
  let result = true;
  tasks.forEach((a) => {
    if (isChild(a) && !a.isExcludeFromGanttChart) {
      result = false;
    }
  });
  return result;
};
export const calculateCalendarDuration = (startDate: Date, endDate: Date) => {
  if (isSameDayOfYear(startDate, endDate)) {
    return GANTT_WORKING_HOURS;
  } else {
    return diffTimeHours(startDate, WORK_TIME_END)
      + diffDays(addDays(normalizeDate(startDate), 1), normalizeDate(endDate)) * GANTT_WORKING_HOURS
      + diffTimeHours(WORK_TIME_START, endDate);
  }
};
